import React from 'react';
import { Box, Flex, keyframes, Skeleton, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SEHomeCard } from '../../../../common/components/SEHomeCard';
import { CYCLE_COLOR_TRANSITION, useCycleColors } from '../../../../common/hooks/use-cycle-color';
import { EvseType } from '../../types';
import {
  COLOR_AMBER,
  COLOR_AMBER_DARK,
  COLOR_AMBER_DARK_DIM,
  COLOR_AMBER_DIM,
  COLOR_BLUE,
  COLOR_BLUE_DARK,
  COLOR_BLUE_DARK_DIM,
  COLOR_GRAY,
  COLOR_GREEN,
  COLOR_GREEN_DARK,
  COLOR_RED,
} from './banner-colors';

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

const pulseRedAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

// Dim variants for hook-driven pulsing — match the dim stops used for the charger SVG fills.
const DIM_BLUE = 'rgba(30,121,182,0.1)'; // dims COLOR_BLUE  (#1E79B6)
const DIM_RED = 'rgba(186,52,48,0.1)'; //  dims COLOR_RED   (#BA3430)

type BannerWrapperProps = {
  isLoaded: boolean | undefined;
  evseType: EvseType;
  isDeviceActive: boolean;
  isDeviceCapable?: boolean;
  borderColor: string;
  pulseAmber?: boolean;
  pulseBlueAmber?: boolean;
  pulseBlue?: boolean;
  pulseBlueGreen?: boolean;
  pulseRed?: boolean;
  isDeviceStopped?: boolean;
  children: React.ReactNode;
};

const DEVICE_INDICATOR: Record<EvseType, { labelKey: string; route: string; testId: string }> = {
  SmartAmp: { labelKey: 'Account.smart amp', route: 'smart-amp', testId: 'smartamp-indicator' },
  SmartPlug: { labelKey: 'Account.smart plug', route: 'smart-plug', testId: 'smartplug-indicator' },
};

export const BannerWrapper = ({
  isLoaded,
  evseType,
  isDeviceActive,
  isDeviceCapable = true,
  borderColor,
  pulseAmber,
  pulseBlueAmber,
  pulseBlue,
  pulseBlueGreen,
  pulseRed,
  isDeviceStopped,
  children,
}: BannerWrapperProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deviceIndicator = DEVICE_INDICATOR[evseType];
  const activeBlue = useColorModeValue(COLOR_BLUE, COLOR_BLUE_DARK);
  const dimActiveBlue = useColorModeValue(DIM_BLUE, COLOR_BLUE_DARK_DIM);
  const activeGreen = useColorModeValue(COLOR_GREEN, COLOR_GREEN_DARK);
  const activeAmber = useColorModeValue(COLOR_AMBER, COLOR_AMBER_DARK);
  const dimActiveAmber = useColorModeValue(COLOR_AMBER_DIM, COLOR_AMBER_DARK_DIM);
  // All pulses share the module-level timer in use-cycle-color → always in phase with the charger SVG.
  const amberDimColor = useCycleColors([activeAmber, dimActiveAmber], !!pulseAmber);
  const blueYellowColor = useCycleColors([activeBlue, activeAmber], !!pulseBlueAmber);
  const blueDimColor = useCycleColors([activeBlue, dimActiveBlue], !!pulseBlue);
  const blueGreenColor = useCycleColors([activeBlue, activeGreen], !!pulseBlueGreen);
  const redDimColor = useCycleColors([COLOR_RED, DIM_RED], !!pulseRed);
  const activeBorderColor = pulseBlueGreen
    ? blueGreenColor
    : pulseBlueAmber
    ? blueYellowColor
    : pulseBlue
    ? blueDimColor
    : pulseAmber
    ? amberDimColor
    : pulseRed
    ? redDimColor
    : borderColor;

  return (
    <>
      <Flex mt={4} w="100%" align="center" justify="space-between">
        <Text textAlign="left" fontSize="16px" letterSpacing="wider" data-testid="session-status-label">
          {t('EVSE.session status').toUpperCase()}
        </Text>

        {isDeviceCapable && (
          <Flex
            align="center"
            gap={1.5}
            cursor="pointer"
            onClick={() => navigate(`../${deviceIndicator.route}?direction=forward&backURL=../device-status`)}
            data-testid={deviceIndicator.testId}
          >
            <Box
              w="12px"
              h="12px"
              borderRadius="full"
              bg={isDeviceStopped ? COLOR_RED : isDeviceActive ? activeGreen : COLOR_GRAY}
              flexShrink={0}
              animation={
                isDeviceStopped
                  ? `${pulseRedAnimation} 1.5s ease-in-out infinite`
                  : isDeviceActive
                  ? `${pulseAnimation} 1.5s ease-in-out infinite`
                  : undefined
              }
            />
            <Text
              fontSize="16px"
              letterSpacing="0"
              color={activeBlue}
              fontFamily="-apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif"
            >
              {t(deviceIndicator.labelKey)}
            </Text>
          </Flex>
        )}
      </Flex>

      <Skeleton isLoaded={!!isLoaded} borderRadius={5} borderBottomRightRadius="25px" mt={2} w="100%">
        <SEHomeCard
          borderTopLeftRadius={5}
          borderBottomRightRadius="25px"
          borderLeft={`8px solid ${activeBorderColor}`}
          transition={`border-left-color ${CYCLE_COLOR_TRANSITION}`}
          py={3}
          px={3}
          _dark={{ bg: '#363E40' }}
          fontFamily="-apple-system, 'SF Pro Text', BlinkMacSystemFont, sans-serif"
          data-testid="multipurpose-banner-card"
        >
          {children}
        </SEHomeCard>
      </Skeleton>
    </>
  );
};
